import * as React from 'react'
import { useCallback, useRef, useState } from 'react'

import {
  clearSearch,
  getSearchResults,
  updateSearchParams,
} from '@disco/state-management/actions/SearchActions'

import type { FilterState } from 'types/filter-page'

import { Button, Flex, Popover, SearchTextField } from 'components/ds'
import {
  FilterActionAddButton,
  FilterActionClearAllButton,
  SelectedFilterBadges,
} from 'components/search/search-filters'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { useFiltersDefinition } from 'views/pages/search/utils'

import styles from './index.module.css'
import { filterStateToSearchParams } from './filter-state-to-search-params'

/**
 * Prototype topbar search: the unified-search-page look (single input + the
 * generic "+ Filter" badges) wired to the legacy `search` slice so results
 * still render in the explorer/Browse pane. Keyword + similarity only — the
 * getSearchResults thunk auto-detects URLs → similarity, else keyword, so the
 * input needs no explicit type routing.
 */

// Badges shown inline before the rest collapse into "+N more".
const MAX_INLINE_FILTERS = 2
const UnifiedSearch = (): React.ReactElement => {
  const dispatch = useDispatch()
  const searchTerm = useSelector(state => state.search.searchTerm)
  const definitions = useFiltersDefinition()
  const [filters, setFilters] = useState<FilterState[]>([])

  // Serialized legacy params of the last search we actually ran. Adding a
  // filter shell (e.g. "+ Filter → BPM", before a value is entered) produces
  // no new params — filterStateToSearchParams skips empty-value filters — so
  // gating on this key stops the empty add from firing a premature search.
  const lastSearchKeyRef = useRef<string>(
    JSON.stringify(filterStateToSearchParams([])),
  )

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(updateSearchParams({ searchTerm: e.target.value }))
    },
    [dispatch],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return
      dispatch(updateSearchParams({ searchOpen: true }))
      dispatch(getSearchResults())
    },
    [dispatch],
  )

  const handleFiltersChange = useCallback(
    (next: FilterState[]) => {
      // Always reflect the change in the badges/state, even for an empty
      // filter shell — the value dropdown needs its badge to render.
      setFilters(next)

      const params = filterStateToSearchParams(next)
      const key = JSON.stringify(params)
      // Only touch the search slice / re-run when the effective filters
      // changed. An empty filter shell serializes identically, so we skip it
      // and wait for a value (or a removal) to actually run the search.
      if (key === lastSearchKeyRef.current) return
      lastSearchKeyRef.current = key

      dispatch(updateSearchParams({ ...params, searchOpen: true }))
      dispatch(getSearchResults())
    },
    [dispatch],
  )

  const handleClearAll = useCallback(() => {
    setFilters([])
    // Back to the empty-filter baseline so the next empty shell added after a
    // reset is still recognised as "no change" and doesn't fire a search.
    lastSearchKeyRef.current = JSON.stringify(filterStateToSearchParams([]))
    // Resets query + filters and returns to the default browse panel.
    dispatch(clearSearch(false))
  }, [dispatch])

  // The topbar can't grow past its fixed 46px, so only the first few badges
  // stay inline; the rest collapse into a "+N more" popover.
  const visibleFilters = filters.slice(0, MAX_INLINE_FILTERS)
  const overflowFilters = filters.slice(MAX_INLINE_FILTERS)

  // SelectedFilterBadges edits the array it's handed, so each half has to be
  // spliced back into the full list before it reaches redux — otherwise
  // editing a visible badge would drop every overflowed one (and vice versa).
  const handleVisibleChange = useCallback(
    (next: FilterState[]) => handleFiltersChange([...next, ...overflowFilters]),
    [handleFiltersChange, overflowFilters],
  )

  const handleOverflowChange = useCallback(
    (next: FilterState[]) => handleFiltersChange([...visibleFilters, ...next]),
    [handleFiltersChange, visibleFilters],
  )

  // Mirror the /search page's Reset: surface it whenever there's anything to
  // clear — a query OR any filter — not just when filters exist.
  const hasClearableState = Boolean(searchTerm?.trim()) || filters.length > 0

  return (
    <Flex direction="row" align="center" gap="2" className={styles.wrapper}>
      <SearchTextField
        value={searchTerm ?? ''}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder="Search keywords or paste a link"
        iconPosition="left"
        className={styles.input}
      />
      <div className={styles.addFilter}>
        <FilterActionAddButton
          value={filters}
          definitions={definitions}
          onChange={handleFiltersChange}
        />
      </div>
      <div className={styles.badges}>
        <SelectedFilterBadges
          value={visibleFilters}
          definitions={definitions}
          seedDefaultState={false}
          onChange={handleVisibleChange}
        />
        {overflowFilters.length > 0 && (
          <Popover.Root>
            <Popover.Trigger>
              <Button variant="soft" size="1" className={styles.moreButton}>
                +{overflowFilters.length} more
              </Button>
            </Popover.Trigger>
            <Popover.Content size="1" className={styles.morePopover}>
              <Flex direction="column" align="start" gap="2">
                <SelectedFilterBadges
                  value={overflowFilters}
                  definitions={definitions}
                  seedDefaultState={false}
                  onChange={handleOverflowChange}
                />
              </Flex>
            </Popover.Content>
          </Popover.Root>
        )}
        {hasClearableState && (
          <FilterActionClearAllButton
            definitions={definitions}
            seedDefaultState={false}
            onChange={handleClearAll}
          />
        )}
      </div>
    </Flex>
  )
}

export default UnifiedSearch
